import {Button, message, Popover, Skeleton} from 'antd'
import {course_class_getGradeDetailsByAuth_Api, course_class_getGrades_Api, course_getGradeReport_Api,} from 'apis'
import {getId} from 'apis/model/base'
import {LoginContext} from 'components/LoginContext'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {BiChevronDown, BiSearch, BiSolidEdit, BiTime, BiTrash} from 'react-icons/bi'
import {RiSearchEyeLine} from 'react-icons/ri'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {NullAsDefault} from 'views/Shared'
import {renderCourseClassGrade} from 'views/Workspace/Course/functions/renderCourseClassGrade'
import {GradeActionTypes} from '../..'
import getTitle from '../../../../../helpers/getTitle'
import EntityList from '../../../EntityList'
import {bindQueryParam, bindQueryParams} from '../../../functions/routerHelper'
import CourseClassContext from '../../CourseClassContext'
import usePaginationHelper, {createPagerKey} from "../../../hooks/usePaginationHelper";
import {DateNameSortBy} from "../../../factory/createEntitySortBy";
import {getType} from "../../../../../apis/model/base";
import EmptyHolder from "../../../../../components/EmptyHolder";
import {LayoutContext} from "../../../../../components/layouts/Default/LayoutContext";
import {BsPencilSquare, BsThreeDots} from "react-icons/bs";
import getLinkToDetail from "../../../../../helpers/getLinkToDetail";
import {Link} from "react-router-dom";
import history, {useRouteSearchParam} from "../../../../../envs/_current/history";
import {PiCertificate, PiChartBarFill, PiUserListFill} from "react-icons/pi";
import {TbClockEdit} from "react-icons/tb";
import {useCourseGrades} from "../../functions/useCourseGrades";
import classNames from "classnames";
import dayjs from "dayjs";
import {CourseClassTabHeader, ViewRoleToggle} from "../components/CourseClassTabHeader";
import copyToClipboard from "copy-to-clipboard";
import {IoCopyOutline} from "react-icons/io5";
import {GoArrowRight, GoDash, GoDotFill} from "react-icons/go";
import {ExamOutlineSvg} from "../../../../../components/Svgs/ExamSvg";

const AVATAR_COLORS = ['bg-indigo-500', 'bg-green-500', 'bg-red-400', 'bg-yellow-500', 'bg-purple-500', 'bg-blue-500', 'bg-pink-500', 'bg-teal-500']
const getAvatarColor = (str = '') => {
    let hash = 0
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

const GRADE_COLS_THRESHOLD = 3

export const GradeStudentReport = ({course_class_id, activeSubjectId, passedScore = 0}) => {
    const t = useTranslate()
    const gradeColumnsRef = useRef([])
    const [expandedRows, setExpandedRows] = useState(new Set())
    const [keyword, setKeyword] = useState('')
    const [statusFilter, setStatusFilter] = useState(null)

    React.useEffect(() => {
        setKeyword('')
        setStatusFilter(null)
        setExpandedRows(new Set())
        gradeColumnsRef.current = []
    }, [activeSubjectId])

    const toggleExpand = useCallback((index) => {
        setExpandedRows(prev => {
            const next = new Set(prev)
            next.has(index) ? next.delete(index) : next.add(index)
            return next
        })
    }, [])

    const statusFilters = useMemo(() => [
        {value: null, label: t('all'), color: null},
        {value: 1, label: t('passed'), color: 'green'},
        {value: 0, label: t('failed'), color: 'red'},
        {value: -1, label: t('not yet graded'), color: 'gray'},
    ], [t])

    const renderRow = useCallback((student, index) => {
        const {student_name, student_idcode, score_number, result_status, grade_data: grades = []} = student ?? {}
        const name = student_name || '-'

        if (keyword) {
            const kw = keyword.toLowerCase()
            if (!name.toLowerCase().includes(kw) && !(student_idcode || '').toLowerCase().includes(kw)) return null
        }
        if (statusFilter === 1 && result_status !== 1) return null
        if (statusFilter === 0 && result_status !== 0) return null
        if (statusFilter === -1 && result_status != null) return null

        const initial = name.charAt(0).toUpperCase()
        const isExpanded = expandedRows.has(index)

        if (index === 0 && grades.length > 0) {
            gradeColumnsRef.current = grades.map(g => ({id: g.grade_id, title: g.title}))
        }

        const colDefs = gradeColumnsRef.current
        const numCols = colDefs.length || grades.length
        const hasMany = numCols > GRADE_COLS_THRESHOLD

        let result_label = t('not yet graded'), result_cls = 'text-color-400'
        if (result_status === 1) { result_label = t('passed'); result_cls = 'bg-green-50 text-green-600' }
        else if (result_status === 0) { result_label = t('failed'); result_cls = 'bg-red-50 text-red-500' }

        let score_color = 'text-color-300'
        if (score_number != null) {
            if (passedScore > 0) {
                score_color = score_number >= passedScore ? 'text-green-600' : 'text-red-500'
            } else {
                if (score_number >= 8) score_color = 'text-green-600'
                else if (score_number >= 5) score_color = 'text-yellow-500'
                else score_color = 'text-red-500'
            }
        }

        const avatarCell = (
            <div className="flex items-center gap-2">
                <div
                    className={classNames(
                        'w-6 h-6 rounded-full flex flex-center text-white text-xs font-bold flex-shrink-0',
                        getAvatarColor(name))
                    }>
                    {initial}
                </div>
                <div className="flex flex-col gap-0.5">
                    <span className="font-bold text-sm text-color-000 truncate tracking-wide">
                        {name}
                    </span>
                    {student_idcode && (
                        <span
                            onClick={() => {
                                copyToClipboard(student_idcode)
                                message.info(t('copied'))
                            }}
                            className="font-mono text-2xs text-blue-500 hover:text-blue-600 cursor-pointer">
                            #{student_idcode}
                            <IoCopyOutline size={12} className="ml-1"/>
                        </span>
                    )}
                </div>
            </div>
        )
        const scoreVal = <span className={classNames('text-sm font-bold tabular-nums', score_color)}>{score_number ?? '-'}</span>
        const resultBadge = (
            <span className={classNames('text-xs font-semibold px-2 py-0.5 rounded-full whitespace-no-wrap', result_cls)}>
                {result_label}
            </span>
        )

        const popoverContent = (
            <div className="flex flex-col gap-2 min-w-[160px]">
                {grades.map(g => (
                    <div key={g.grade_id} className="flex justify-between gap-4 text-xs">
                        <span className="flex-1 font-medium text-color-100 truncate">{g.title}</span>
                        <span className="font-semibold text-color-100 flex-shrink-0">{g.score_number ?? '-'}</span>
                    </div>
                ))}
            </div>
        )

        if (!hasMany) {
            return (
                <tr key={index} className="border-b border-color-50 hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2">{avatarCell}</td>
                    {grades.length > 0
                        ? grades.map((g, gi) => (
                            <td key={gi} className="px-3 py-2 text-center whitespace-no-wrap">
                                <span className="text-sm text-color-200 111-111">{g.score_number ?? '-'}</span>
                            </td>
                        ))
                        : colDefs.map((_, gi) => (
                            <td key={gi} className="px-3 py-2 text-center whitespace-no-wrap">
                                <span className="text-sm text-color-400">-</span>
                            </td>
                        ))
                    }
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{scoreVal}</td>
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{resultBadge}</td>
                </tr>
            )
        }

        return (
            <React.Fragment key={index}>
                <tr className="border-b border-color-50 hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2">{avatarCell}</td>
                    {colDefs.slice(0, 3).map((g, gi) => grades[gi]?.score_number ? (
                        <td key={gi} className="px-3 py-2 text-center whitespace-no-wrap">
                            <span className="font-bold text-sm text-color-000 222-222">
                                {grades[gi]?.score_number ? grades[gi]?.score_number : ' - '}
                            </span>
                        </td>
                    ) : (
                        <td key={gi} className="px-3 py-2 text-center whitespace-no-wrap cell-2222">
                            <span className="text-sm text-color-200">
                                {' - '}
                            </span>
                        </td>
                    ))}
                    <td className="px-3 py-2 text-center whitespace-no-wrap">
                        <Popover
                            content={popoverContent}
                            title={<span className="text-xs font-semibold">{t('all grades')}</span>}
                            trigger="hover"
                            placement="top">
                            <span className="inline-flex items-center text-xs px-2 py-1 bg-indigo-50 text-indigo-500 rounded-full cursor-pointer hover:bg-indigo-100 transition-colors whitespace-no-wrap">
                                +{numCols - 3}
                            </span>
                        </Popover>
                    </td>
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{scoreVal}</td>
                    <td className="px-3 py-2 text-center whitespace-no-wrap">{resultBadge}</td>
                    <td className="px-2 py-2 text-center whitespace-no-wrap">
                        <button
                            style={{ padding: '0.25rem', borderRadius: '0.375rem' }}
                            className="flex flex-center bg-gray-50 hover:bg-gray-100 text-color-300 hover:text-color-100 transition-all duration-300"
                            onClick={() => toggleExpand(index)}>
                            <BiChevronDown size={16} className={isExpanded ? 'rotate-180' : ''}/>
                        </button>
                    </td>
                </tr>
                {isExpanded && (
                    <tr className="bg-indigo-50/20 border-b border-color-50">
                        <td className="px-3 py-2"/>
                        <td colSpan={grades.length + (isExpanded ? 4 : 3)} className="p-3">
                            <div className="flex flex-wrap gap-2">
                                {grades.map(g => (
                                    <div key={g.grade_id} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-color-100 min-w-[80px]">
                                        <span
                                            className={classNames(
                                                "w-7 h-7 flex flex-center rounded-lg font-bold text-sm",
                                                g.score_number ? 'border-2 border-green-400 text-green-500' : 'border border-color-100 text-color-200'
                                            )}>
                                            {g.score_number ?? <GoDash />}
                                        </span>
                                        <span
                                            className={classNames(
                                                "text-xs max-lines-2 max-w-[100px] leading-tight",
                                                g.score_number ? 'font-medium text-gray-700' : 'text-color-200'
                                            )}>
                                            {g.title}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        )
    }, [t, keyword, statusFilter, expandedRows, toggleExpand, passedScore])


    const renderTable = useCallback((child) => {
        const gradeCols = gradeColumnsRef.current
        const hasMany = gradeCols.length > GRADE_COLS_THRESHOLD

        return (
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead>
                        <tr className="background-100 border-b border-color-100">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-color-300 uppercase tracking-wider">{t('student')}</th>
                            {!hasMany
                                ? gradeCols.map(col => (
                                    <th key={col.id} className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider gradeCols">
                                        {col.title || 1111}
                                    </th>
                                ))
                                : (
                                    <>
                                        {gradeCols.slice(0, 3).map(col => (
                                            <th key={col.id} className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider gradeColsMore">
                                                {col.title  || 222}
                                            </th>
                                        ))}
                                        <th className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider whitespace-no-wrap">
                                            {t('grades')}
                                        </th>
                                    </>
                                )
                            }
                            <th className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider whitespace-no-wrap">
                                {t('score')}
                            </th>
                            <th className="px-3 py-2 w-px text-center text-xs font-semibold text-color-300 uppercase tracking-wider whitespace-no-wrap">
                                {t('outcome')}
                            </th>
                            {hasMany && <th className="px-2 py-2 w-px"/>}
                        </tr>
                    </thead>
                    <tbody className="background divide-y divide-color-50">
                        {child}
                    </tbody>
                </table>
            </div>
        )
    }, [t])

    if (!activeSubjectId) return <EmptyHolder title={null}/>

    return (
        <div className="flex flex-col gap-3">
            {/* Status filter + threshold */}
            <div className="flex flex-wrap gap-1.5">
                {statusFilters.map((f, i) => {
                    const isActive = statusFilter === f.value
                    const cls = isActive
                        ? f.color === 'green' ? 'bg-green-600 border-green-600 text-white'
                            : f.color === 'red' ? 'bg-red-500 border-red-500 text-white'
                                : f.color === 'gray' ? 'bg-color-300 border-color-300 text-white'
                                    : 'bg-indigo-600 border-indigo-600 text-white'
                        : f.color === 'green' ? 'border-color-200 text-green-600 hover:border-green-500'
                            : f.color === 'red' ? 'border-color-200 text-red-500 hover:border-red-400'
                                : 'border-color-200 text-color-300 hover:border-indigo-400 hover:text-indigo-500'
                    return (
                        <button
                            key={i}
                            onClick={() => setStatusFilter(isActive ? null : f.value)}
                            className={classNames('px-3 py-1 text-xs font-medium rounded-lg border transition-colors select-none', cls)}>
                            {f.label}
                        </button>
                    )
                })}
                {passedScore > 0 && (
                    <span className="text-xs text-green-600 font-medium italic self-center">
                        {`* ${t('passed score')} : ≥${passedScore}`}
                    </span>
                )}
            </div>

            {/* Search */}
            <div className="relative">
                <BiSearch size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-color-400 pointer-events-none"/>
                <input
                    type="text"
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    placeholder={t('search by name or student code')}
                    className="w-full pl-7 pr-6 py-2 text-xs border border-color-200 rounded-lg focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 background text-color-100 placeholder-color-400 transition-colors"
                />
                {keyword && (
                    <button
                        onClick={() => setKeyword('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-200 hover:bg-gray-300 text-gray-500 text-xs transition-colors">
                        ×
                    </button>
                )}
            </div>

            <EntityList
                key={[course_class_id, activeSubjectId].join('-')}
                refreshKey={[course_class_id, activeSubjectId].join('-')}
                RenderEntity={React.Fragment}
                renderEmpty={() => <EmptyHolder title={null}/>}
                renderItem={renderRow}
                renderWrap={renderTable}
                renderLoading={() => (
                    <>
                        {[...Array(5)].map((_, i) => (
                            <tr key={i} className="border-b border-color-50">
                                <td className="px-3 py-2">
                                    <div className="flex items-center gap-2">
                                        <Skeleton.Avatar active size={28}/>
                                        <Skeleton.Input active size="small" style={{width: 100, height: 14}}/>
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-center"><Skeleton.Input active size="small" style={{width: 40, height: 14}}/></td>
                                <td className="px-3 py-2 text-center"><Skeleton.Input active size="small" style={{width: 50, height: 14}}/></td>
                            </tr>
                        ))}
                    </>
                )}
                query={bindQueryParams([{id: course_class_id}, {subject_id: activeSubjectId}])}
                apiInfo={course_getGradeReport_Api}
            />
        </div>
    )
}

// Export - tái dùng NGUYÊN ở tab "Điểm số & Kết quả" (CourseClassMyGradeResultTab.js, self-service,
// hiện cho học viên/phụ huynh) làm phần danh sách chi tiết từng cột điểm - tránh dựng lại 1 danh
// sách grade item song song trong khi board này đã xử lý đủ (điểm/trạng thái đạt/thi trực tuyến).
export const MyBoard = ({course_class_id}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const login = useContext(LoginContext)
    const [items, setItems] = useState([])
    const [myRelationship, setMyRelationship] = useState(null)
    const [loading, setLoading] = useState(false)

    const isSelf = !myRelationship || myRelationship === 'SELF'

    const {handleAsyncAction: loadGrades} = useAsyncAction({
        apiInfo: course_class_getGradeDetailsByAuth_Api,
        query: {id: course_class_id},
        onSuccess: (result, {response}) => {
            const resp = (response && response.data) || {}
            setItems(resp.data || [])
            setMyRelationship(resp.my_relationship || null)
            setLoading(false)
        },
        onError: () => setLoading(false),
    })

    useEffect(() => {
        if (!login || !course_class_id) return
        setLoading(true)
        loadGrades()
    }, [course_class_id])

    const activeExamItems = useMemo(
        () => isSelf
            ? items.filter(item => {
                const exam = item.grade && item.grade.course_class_exam
                if (!(exam && exam.status === 1)) return false
                // Còn cho "làm bài (tiếp)" khi CÒN LƯỢT - không giới hạn (max_attempts = 0) hoặc
                // used_attempts < max_attempts. Trước đây gate theo "chưa có điểm" nên bài cho làm
                // nhiều lần: làm xong lượt 1 có điểm là thẻ biến mất dù còn lượt (bạn báo).
                // CourseClassExamTransformerTiny nay có trả max_attempts/used_attempts.
                if (exam.max_attempts != null) {
                    return exam.max_attempts === 0 || (exam.used_attempts ?? 0) < exam.max_attempts
                }
                // Fallback khi BE chưa trả số lượt: giữ mốc cũ "chưa có điểm".
                const s = item.score_type === 'number' ? item.final_score_number : item.final_score_text
                return s === null || s === undefined || s === ''
            })
            : [],
        [items, isSelf]
    )

    if (!login) return null

    if (loading) {
        return (
            <div className="flex flex-col gap-2">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 border-l-4 border-gray-100 rounded-r-xl bg-white">
                        <Skeleton.Avatar active size={40} shape="square"/>
                        <div className="flex-1">
                            <Skeleton.Input active size="small" style={{width: 160, height: 14}}/>
                            <div className="mt-1"><Skeleton.Input active size="small" style={{width: 100, height: 12}}/></div>
                        </div>
                        <Skeleton.Input active size="small" style={{width: 60, height: 26}}/>
                    </div>
                ))}
            </div>
        )
    }

    // Empty state "chưa có cột điểm nào" - trước đây chỉ có icon khay rỗng trơn, KHÔNG giải thích gì
    // (bạn báo: nhìn vào không biết khi nào tab này có data, data là loại gì). Không bịa ra "lộ trình
    // đánh giá" theo từng bước cụ thể (vd tên bài thi/thứ tự mở khoá) vì course_class_getGradeDetailsByAuth_Api
    // KHÔNG trả cấu trúc bài thi đã lên kế hoạch cho lớp này - chỉ mô tả CHUNG các LOẠI điểm hệ thống
    // này hỗ trợ (điểm danh chuyên cần + điểm kiểm tra/thi) và ĐIỀU KIỆN xuất hiện (GV nhập xong),
    // đều là thông tin đúng cho MỌI lớp, không riêng lớp nào.
    if (!items.length) {
        return (
            <EmptyHolder
                icon={() => (
                    <div className="w-16 h-16 rounded-2xl bg-amber-50 flex flex-center">
                        <PiCertificate size={30} className="text-amber-500"/>
                    </div>
                )}
                title={t('no grade columns published yet')}
                subTitle={t('grade columns will appear automatically description')}
            />
        )
    }

    return (
        <div className="flex flex-col gap-4">
            {!isSelf && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
                    <span className="text-xs text-blue-500 font-medium">
                        {t('Viewing as parent')}
                    </span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-semibold">
                        {t(myRelationship)}
                    </span>
                </div>
            )}
            {activeExamItems.length > 0 && (
                <div className="flex flex-col gap-4">
                    {activeExamItems.map((examItem, i) => {
                        const exam = examItem.grade.course_class_exam
                        const gradeTitle = getTitle(examItem.grade)
                        const usedAttempts = exam.used_attempts ?? 0
                        const attemptedBefore = exam.max_attempts != null && usedAttempts > 0
                        const attemptsLeft = exam.max_attempts && exam.max_attempts > 0
                            ? exam.max_attempts - usedAttempts
                            : null
                        return (
                            <div
                                key={i}
                                onClick={() => history.push('/course-class-exam/' + exam.id)}
                                style={{ padding: '0.75rem', cursor: 'pointer' }}
                                className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-violet-600 to-indigo-500  shadow-md hover:shadow-lg transition-all">
                                {isLg && (
                                    <div className="w-12 h-12 flex-shrink-0 flex flex-center bg-white-40 rounded-xl">
                                        <ExamOutlineSvg size={22} className="text-white"/>
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="mb-1 flex items-center gap-1.5 flex-wrap">
                                        <span className="px-2 py-0.5 bg-white-20 rounded-md font-bold text-xs md:text-2xs text-white-80 uppercase tracking-wide">
                                            {t('exam available')}
                                        </span>
                                        {attemptsLeft != null && (
                                            <span className="px-2 py-0.5 bg-white-20 rounded-md font-semibold text-2xs text-white-80">
                                                {t('attempts left', {n: attemptsLeft})}
                                            </span>
                                        )}
                                    </div>
                                    <p className="font-bold text-base text-white hover:text-primary leading-tight truncate">
                                        {gradeTitle}
                                    </p>
                                    {exam.title && exam.title !== gradeTitle && (
                                        <p className="text-xs text-indigo-100 truncate mt-0.5">{exam.title}</p>
                                    )}
                                    <div className="flex items-center gap-3 mt-1.5">
                                        {exam.duration_minutes > 0 && (
                                            <span className="flex items-center gap-1 font-medium text-xs text-white-60">
                                                <BiTime size={12} className="inline mr-0.5"/>
                                                {exam.duration_minutes} {t('minutes')}
                                            </span>
                                        )}
                                        {exam.max_score > 0 && (
                                            <span className="text-xs text-white-60">
                                                <GoDotFill size={10} className="inline mr-0.5"/>
                                                {exam.max_score} {t('points')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="px-3 py-1.5 flex items-center gap-1 flex-shrink-0 bg-white hover:bg-white-40 text-secondary-600 rounded-xl text-xs font-semibold shadow-sm">
                                    {attemptedBefore ? t('retake exam') : t('Take exam')} <GoArrowRight size={16} className="text-secondary-600"/>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
            {items.map((item, index) => {
                const {grade, score_type, score_number, score_1_number, score_2_number, score_text, final_score_number, final_score_text, final_grade_date} = item ?? {}

                const dateObj = final_grade_date ? dayjs(final_grade_date) : (grade && grade.grade_date ? dayjs(grade.grade_date) : null)
                const hasTime = dateObj && (dateObj.hour() !== 0 || dateObj.minute() !== 0)

                const displayScore = score_type === 'number' ? final_score_number : final_score_text
                const hasScore = displayScore !== null && displayScore !== undefined && displayScore !== ''

                const passedScore = score_type === 'number'
                    ? (score_2_number ? ((grade && grade.passed_score_2) || (grade && grade.passed_score))
                        : score_1_number ? ((grade && grade.passed_score_1) || (grade && grade.passed_score))
                        : (grade && grade.passed_score)) || null
                    : null
                const isPass = (passedScore != null && final_score_number != null)
                    ? final_score_number >= passedScore
                    : null

                const scoreColor = !hasScore ? 'text-color-400 bg-color-50 border-color-200'
                    : (score_type === 'number' && isPass === true) ? 'text-green-700 bg-green-50 border-green-300'
                    : (score_type === 'number' && isPass === false) ? 'text-red-600 bg-red-50 border-red-300'
                    : (score_type === 'number') ? 'text-color-200 bg-color-50 border-color-200'
                    : 'text-indigo-600 bg-indigo-50 border-indigo-300'

                const colorClass = !hasScore ? 'border-gray-200'
                    : (score_type === 'number' && isPass === true) ? 'border-green-400 bg-green-50'
                    : (score_type === 'number' && isPass === false) ? 'border-red-400 bg-primary-50'
                    : 'border-gray-200 background-100'

                const hasActiveExam = grade && grade.course_class_exam && grade.course_class_exam.status === 1
                const examId = hasActiveExam && grade.course_class_exam.id
                const canTakeExam = hasActiveExam && isSelf && !hasScore

                const rowContent = (
                    <>
                        {dateObj && (
                            <div
                                style={{width: 64, height: 64}}
                                className={classNames(
                                    "p-2 flex flex-col flex-center flex-shrink-0 rounded-xl border-2",
                                    colorClass
                                )}>
                                <span className="font-bold text-xl leading-none">
                                    {dateObj.format('DD')}
                                </span>
                                <span className="font-medium text-xs text-slate-400 leading-tight mt-0.5">
                                    {dateObj.format('MM/YYYY')}
                                </span>
                            </div>
                        )}

                        <div className="flex flex-col space-y-1 flex-1 min-w-0">
                            <span className="font-bold text-base text-color-000 truncate 222-222">
                                {getTitle(grade)}
                            </span>
                            <div className="flex items-center gap-1.5 text-xs text-color-400 leading-tight">
                                {grade && grade.subject && (
                                    <span className="font-medium text-color-300">{getTitle(grade.subject)}</span>
                                )}
                                {grade && grade.subject && grade.score_weight > 0 && <span>•</span>}
                                {grade && grade.score_weight > 0 && (
                                    <span className="italic">{t('score_weight')}: {grade.score_weight}</span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            {canTakeExam ? (
                                <span className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-bold shadow-sm">
                                    <BsPencilSquare size={11}/>{t('Take exam')}
                                </span>
                            ) : (
                                <div className="flex flex-col items-center gap-0.5">
                                    <div className={classNames("text-xs font-bold px-2.5 py-1 rounded-full border", scoreColor)}>
                                        {hasScore ? (score_type === 'number' ? final_score_number : final_score_text) : t('undefined')}
                                    </div>
                                    {hasActiveExam && isSelf && hasScore && (
                                        <span className="text-2xs text-green-500 font-medium">{t('Graded')}</span>
                                    )}
                                    {hasTime && (
                                        <span className="mt-0.5 font-medium text-2xs text-slate-500 italic leading-tight">
                                            {dateObj.format('HH:mm')}
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )

                return canTakeExam ? (
                    <div
                        key={index}
                        role="button"
                        onClick={() => history.push('/course-class-exam/' + examId)}
                        className="flex items-center gap-3 p-3 border border-color-50 background shadow-sm hover:shadow-md rounded-xl cursor-pointer 222-222">
                        {rowContent}
                    </div>
                ) : (
                    <div key={index} className="flex items-center gap-3 p-3 border border-color-50 background shadow-sm rounded-xl 111-111">
                        {rowContent}
                    </div>
                )
            })}
        </div>
    )
}

const AdminActiveExamsBanner = ({course_class_id, refreshKey}) => {
    const t = useTranslate()
    const {isLg} = useContext(LayoutContext)
    const [examGrades, setExamGrades] = useState([])

    const {handleAsyncAction: fetchExamGrades} = useAsyncAction({
        apiInfo: course_class_getGrades_Api,
        onSuccess: (result, {response}) => {
            const items = (response && response.data && response.data.data) || []
            setExamGrades(items.filter(g => g.course_class_exam && g.course_class_exam.status === 1))
        },
    })

    useEffect(() => {
        if (!course_class_id) return
        fetchExamGrades({}, {id: course_class_id})
    }, [course_class_id, refreshKey])

    if (!examGrades.length) return null

    return (
        <div className="flex flex-col gap-2 mb-1">
            {examGrades.map((grade, i) => {
                const exam = grade.course_class_exam
                return (
                    <Link
                        key={i}
                        to={getLinkToDetail(exam)}
                        className="p-3 flex items-center gap-3 border-2 border-secondary-500 shadow-md hover:shadow-lg rounded-2xl transition-all no-underline">
                        {isLg && (
                            <div
                                style={{ background: 'linear-gradient(to right,  #c4b5fd 0%, #A5B4FC 100%)' }}
                                className="w-12 h-12 flex-shrink-0 flex flex-center rounded-xl">
                                <ExamOutlineSvg size={22} className="text-white-70"/>
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="mb-1 px-2 py-0.5 w-fit bg-gradient-to-r from-violet-600 to-indigo-500 rounded-md font-bold text-xs md:text-2xs text-white-80 uppercase tracking-wide">
                                {t('exam available')}
                            </p>
                            <p className="font-bold text-base leading-tight truncate">
                                {getTitle(grade)}
                            </p>
                            <div className="flex items-center gap-3 mt-1">
                                {exam.duration_minutes > 0 && (
                                    <span className="font-medium text-xs text-color-200">
                                        <BiTime size={12} className="inline mr-0.5 text-slate-400"/>
                                        {exam.duration_minutes} {t('minutes')}
                                    </span>
                                )}
                                {exam.max_score > 0 && (
                                    <span className="font-medium text-xs text-color-200">
                                        <GoDotFill size={10} className="inline mr-0.5 text-slate-400"/>
                                        {exam.max_score} {t('points')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div
                            style={{ padding: isLg ? '0.375rem 0.75rem' : '0.5rem', borderRadius: '0.5rem', cursor: 'pointer' }}
                            className="flex flex-center gap-1 flex-shrink-0 bg-secondary-50 hover:bg-secondary-100 font-semibold text-xs text-secondary-600 transition-all duration-300">
                            {isLg && t('manage exam')} <GoArrowRight size={16} className="text-secondary-600"/>
                        </div>
                    </Link>
                )
            })}
        </div>
    )
}

const CourseClassGradeTab = ({
                                 filter_actions = {
                                     sortBy: 'sort-by',
                                     keyword: 'keyword',
                                     course_id: 'course_id',
                                     subject_id: 'subject-id',
                                 },
                             }) => {
    const t = useTranslate()

    const {isSm} = useContext(LayoutContext)
    const [current, setCurrent] = useState('0')

    const {item} = useContext(CourseClassContext)

    const sortBy = useRouteSearchParam(filter_actions.sortBy)

    const can_edit = !!item?.edit || !!item?.available_actions?.can_grade
    // Toggle "Quản trị/Học viên" CHỈ hiện khi giữ ĐỒNG THỜI 2 vai trò trong lớp (vừa quản trị vừa
    // là học viên - is_class_member) - đúng quy tắc đã áp dụng ở CourseClassAttendanceTab.js.
    const is_class_member = !!item?.is_class_member
    const showRoleToggle = can_edit && is_class_member && item?.is_class_leader === false
    const [viewAsRole, setViewAsRole] = useState(can_edit ? 'admin' : 'student')

    const course_class_id = getId(item)

    const owner_id = getId(item?.course)

    const owner_type = getType(item?.course)

    const classSubjects = useMemo(
        () => (item?.class_subjects ?? []).filter(cs => !!cs?.subject),
        [item?.class_subjects]
    )
    const subjects = useMemo(() => classSubjects.map(cs => cs.subject), [classSubjects])

    const [activeSubjectId, setActiveSubjectId] = useState(() => getId(subjects[0]) || null)

    const selectedCs = useMemo(
        () => classSubjects.find(cs => String(getId(cs.subject)) === String(activeSubjectId)),
        [classSubjects, activeSubjectId]
    )
    const passedScore = selectedCs?.passed_score ?? 0


    const {
        modal,
        refresh_token,
        handleAction = NullAsDefault,
    } = useCourseGrades({course_class: item})

    const refreshKey = useMemo(
        () => [refresh_token, course_class_id, activeSubjectId].join(''),
        [refresh_token, course_class_id, activeSubjectId]
    )

    const renderContextMenu = useCallback(
        (item) =>
            can_edit
                ? createContextMenu(
                [
                    // {
                    //     icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                    //     label: 'edit exam',
                    //     key: GradeActionTypes.COURSE_CLASS_EXAM_UPDATE,
                    //     invisible: !!!item?.course_class_exam
                    // },
                    {
                        icon: <RiSearchEyeLine size={14} className="text-green-600"/>,
                        label: 'view grades',
                        key: GradeActionTypes.GRADE_DETAIL,
                    },
                    {
                        icon: <BiSolidEdit size={14} className="text-blue-500"/>,
                        label: 'edit',
                        key: GradeActionTypes.GRADE_UPDATE,
                    },
                    {
                        icon: <TbClockEdit size={14} className="text-color-400"/>,
                        label: 'history',
                        key: GradeActionTypes.GRADE_HISTORY,
                    },
                    {
                        icon: <BiTrash size={14} className="text-primary-300"/>,
                        label: 'delete',
                        key: GradeActionTypes.GRADE_DELETE,
                    },
                ].filter((e) => item && !!!e?.invisible),
                (key) => {
                    handleAction(key, item)
                },
                {
                    icon: (
                        <div
                            style={{width: 22, height: 22, borderRadius: 999}}
                            className="flex flex-center background hover:background-200 text-color-500 hover:text-color-100 cursor-pointer">
                            <BsThreeDots size={12}/>
                        </div>
                    ),
                }
                )
                : undefined,
        [can_edit]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {
            owner_id,
            owner_type,
        },
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({subject_id: activeSubjectId})
            onSortChange(sortBy)
        },
        pagerInfo: {
            PaginationMeta: NullAsDefault,
            RenderEntity: React.Fragment,
            apiInfo: course_class_getGrades_Api,
            query: bindQueryParam({id: course_class_id}),
            renderItem: renderCourseClassGrade({
                renderContextMenu,
                handleAction,
            }),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            )
        },
        renderSearchBar: NullAsDefault,
        SortByWidget: DateNameSortBy,
        query_params: filter_actions,
        sortHeader: t('list of grades'),
        withKey: createPagerKey(refreshKey),
        dependencies: [activeSubjectId],
        // AdvancedFilter (panel "Chọn môn học" nổi lên tự động) đã BỎ - trùng chức năng với hàng
        // chip lọc môn học (subjectFilter) ngay bên trên, lại còn tự bật lên do usePaginationHelper
        // auto-toggle khi filter có giá trị.
        // renderFilter = null: ExpandablePanel bọc filter_bar vẫn tự render khung rỗng dù
        // AdvancedFilter không có nội dung (isToggle vẫn true do auto-toggle) - tắt hẳn khung này.
        renderFilter: () => null,
        isToggleDefault: false,
    })

    const subjectFilter = classSubjects.length > 0 && (
        <div className="flex gap-2 flex-wrap">
            {classSubjects.map((cs) => {
                const sid = getId(cs.subject)
                const isActive = String(activeSubjectId) === String(sid)
                const ps = cs.passed_score
                return (
                    <button
                        key={sid}
                        onClick={() => setActiveSubjectId(sid)}
                        style={{height: 30}}
                        className={classNames(
                            'px-3 text-xs font-semibold rounded-lg transition-all duration-300',
                            isActive
                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                : 'border border-color-200 hover:border-indigo-400 background text-color-300 hover:text-indigo-500'
                        )}>
                        <span>{cs.subject?.title}</span>
                        {ps > 0 && (
                            <span className={classNames('ml-2 text-2xs font-normal', isActive ? 'text-indigo-200' : 'text-color-400')}>
                                {`≥${ps}`}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )

    const adminTabs = [
        {key: '0', icon: <PiChartBarFill size={12}/>, label: t('grades')},
        // Trước dùng chung key 'students' ("Học viên") - dễ nhầm với tab "Học viên" của lớp, trong
        // khi đây là bảng THỐNG KÊ điểm theo từng học viên (khớp cách đặt tên đã dùng ở tab Điểm danh).
        {key: '1', icon: <PiUserListFill size={12}/>, label: t('grades stats by student')},
    ]


    return (
        <div className="flex flex-col gap-3">
            {/* Bố cục khớp CourseClassAttendanceTab.js: toggle vai trò (nếu có) đứng riêng 1 dòng
                trên cùng, dưới đó là banner mô tả nội dung màn hình theo đúng vai trò đang xem, rồi
                mới tới thanh sub-tab. */}
            {can_edit && item?.is_class_leader === false && (
                <div className="px-3 py-2 flex items-center justify-between gap-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                    <div className="flex items-center gap-2 min-w-0">
                        <PiChartBarFill size={14} className="text-indigo-500 flex-shrink-0"/>
                        <span className="text-xs text-indigo-700 leading-snug">
                            {t(viewAsRole === 'admin' ? 'this tab is for grades admin only' : 'viewing grades as a student')}
                        </span>
                    </div>
                    {showRoleToggle && (
                        <ViewRoleToggle role={viewAsRole} onChange={setViewAsRole}/>
                    )}
                </div>
            )}
            {viewAsRole === 'admin' && can_edit && item?.is_class_leader === false && (
                <CourseClassTabHeader
                    tabs={adminTabs}
                    activeKey={current}
                    onTabChange={setCurrent}
                    showTabs={true}
                />
            )}

            {viewAsRole === 'admin' && can_edit && item?.is_class_leader === false ? (
                <>
                    {current === '0' && (
                        <div className="w-full flex flex-col flex-1 gap-3">
                            <AdminActiveExamsBanner
                                course_class_id={course_class_id}
                                refreshKey={refreshKey}
                            />
                            <div className="flex items-center justify-between gap-2 py-1">
                                <div className="flex flex-col py-1">
                                        <span className="font-semibold text-base text-gray-700 tracking-wide">
                                            {t('list of grades')}
                                        </span>
                                    <div className="w-20 h-1 my-1 rounded-full bg-gray-400"/>
                                    <span className="text-xs text-color-300 tracking-wider">{t('create grades description')}</span>
                                </div>
                                <Button
                                    style={{padding: isSm ? "0 12px" : "0 18px"}}
                                    type="primary"
                                    className="button-rounded-md no-border flex flex-center font-medium text-xs md:text-sm"
                                    onClick={() => handleAction(GradeActionTypes.GRADE_CREATE)}>
                                    {t("create")}
                                </Button>
                            </div>
                            {subjectFilter}
                            {activeSubjectId
                                ? <div className="flex flex-col gap-3">{pager_widget}</div>
                                : <EmptyHolder title={t('please select a subject')}/>
                            }
                        </div>
                    )}
                    {current === '1' && (
                        <div className="w-full flex flex-col flex-1 gap-3">
                            {subjectFilter}
                            <GradeStudentReport
                                key={activeSubjectId}
                                course_class_id={course_class_id}
                                activeSubjectId={activeSubjectId}
                                passedScore={passedScore}/>
                        </div>
                    )}
                </>
            ) : (
                <div className="w-full flex flex-col flex-1 gap-3 py-1">
                    <MyBoard course_class_id={course_class_id}/>
                </div>
            )}

            {modal}
        </div>
    )
}

export default CourseClassGradeTab
